// Import users from Excel/CSV: preview with org-chart matching, random passwords, credentials sheet and printable slips.
import React, { useMemo, useRef, useState } from 'react';
import { api } from '../../api';
import { Combobox, Modal, Switch, useToast } from '../../ui';
import { Icon } from '../../ui/Icon';

interface DepRef { text: string; id: number | null; name: string | null; level: 'exact' | 'similar' | 'none' }
interface Row {
  row: number; displayName: string; username: string; email?: string | null; function?: string | null; departments: DepRef[];
  isHead: boolean; roles: string[]; status: 'new' | 'exists' | 'error'; existingId?: number | null; issues: string[]; include?: boolean;
}
interface Parsed { rows: Row[]; unknownDepartments: string[]; departments: { id: number; name: string }[] }
interface Result { displayName: string; username: string; password?: string; email?: string; departments?: string[]; isHead?: boolean; roles?: string[]; status: string; error?: string }

const ROLE_OPTS = [{ value: 'DEPARTMENT', label: 'Compartiment' }, { value: 'REGISTRY', label: 'Registratură' }, { value: 'ADMIN', label: 'Administrator' }];
const roleLabel = (r: string) => ROLE_OPTS.find(o => o.value === r)?.label || r;

export function UsersImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [phase, setPhase] = useState<'choose' | 'reading' | 'preview' | 'saving' | 'done'>('choose');
  const [p, setP] = useState<Parsed | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});      // unknown dept text → "new" | "skip" | "<id>"
  const [newParent, setNewParent] = useState<number | null>(null);
  const [mustChange, setMustChange] = useState(true);
  const [results, setResults] = useState<Result[]>([]);
  const input = useRef<HTMLInputElement>(null);

  const read = async (f: File) => {
    setPhase('reading');
    try {
      const x = await api.uploadTo<Parsed>('/api/auth/users/import/parse', f);
      setP(x);
      setRows(x.rows.map(r => ({ ...r, include: r.status === 'new' })));
      const m: Record<string, string> = {};
      x.unknownDepartments.forEach(d => { m[d] = 'new'; });
      setMapping(m);
      setPhase('preview');
    } catch (e: any) { toast(e.message, 'error'); setPhase('choose'); }
  };
  const upd = (i: number, patch: Partial<Row>) => setRows(l => l.map((r, j) => j === i ? { ...r, ...patch } : r));
  const selected = rows.filter(r => r.include && r.status !== 'error');
  const dupUsers = useMemo(() => { const c = new Map<string, number>(); selected.forEach(r => c.set(r.username, (c.get(r.username) || 0) + 1)); return new Set([...c].filter(([, n]) => n > 1).map(([u]) => u)); }, [rows]);

  const doImport = async () => {
    if (dupUsers.size) { toast('Există nume de utilizator duplicate în listă: ' + [...dupUsers].join(', '), 'error'); return; }
    setPhase('saving');
    try {
      const body = {
        mustChangePassword: mustChange, newDepartmentsParentId: newParent,
        rows: selected.map(r => {
          const ids: number[] = [], news: string[] = [];
          for (const d of r.departments) {
            if (d.id) { ids.push(d.id); continue; }
            const m = mapping[d.text] || 'new';
            if (m === 'new') news.push(d.text); else if (m !== 'skip') ids.push(Number(m));
          }
          return { displayName: r.displayName, username: r.username, email: r.email, departmentIds: [...new Set(ids)], newDepartments: news, isHead: r.isHead, roles: r.roles };
        }),
      };
      const x = await api.post<{ created: number; skipped: number; newDepartments: number; results: Result[] }>('/api/auth/users/import', body);
      setResults(x.results); setPhase('done');
      toast(`${x.created} conturi create${x.newDepartments ? `, ${x.newDepartments} unități noi` : ''}`, 'ok');
      onDone();
    } catch (e: any) { toast(e.message, 'error'); setPhase('preview'); }
  };

  const created = results.filter(r => r.status === 'created');
  const withEmail = created.filter(r => r.email && /@/.test(r.email));
  const [mailed, setMailed] = useState<number | null>(null);
  const [mailing, setMailing] = useState(false);
  const sendMail = async () => {
    setMailing(true);
    try { const x = await api.post<{ queued: number }>('/api/mail/credentials', { rows: withEmail }); setMailed(x.queued); toast(`${x.queued} e-mailuri puse în coada de trimitere`, 'ok'); }
    catch (e: any) { toast(e.message, 'error'); } finally { setMailing(false); }
  };
  const downloadXlsx = () => api.downloadPost('/api/auth/users/import/credentials', { rows: created }, 'conturi-docflow.xlsx').catch(e => toast(e.message, 'error'));
  const printSlips = () => {
    const w = window.open('', '_blank');
    if (!w) { toast('Permiteți ferestrele pop-up pentru a tipări fișele', 'error'); return; }
    const esc = (s: any) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as any)[c]);
    const url = location.origin + location.pathname;
    w.document.write(`<!doctype html><html lang="ro"><head><meta charset="utf-8"><title>Conturi DocFlow</title><style>
      body{font-family:Arial,sans-serif;margin:16px;color:#111} .slip{border:1px dashed #888;border-radius:6px;padding:14px 16px;margin:0 0 12px;page-break-inside:avoid}
      h2{margin:0 0 8px;font-size:16px} table{border-collapse:collapse} td{padding:3px 12px 3px 0;font-size:14px} td.k{color:#555} .pw{font-family:Consolas,monospace;font-size:16px;font-weight:bold;letter-spacing:.5px}
      .note{font-size:12px;color:#444;margin-top:8px} @media print{button{display:none}}</style></head><body>
      <button onclick="print()" style="margin-bottom:12px">Tipărește</button>
      ${created.map(r => `<div class="slip"><h2>DocFlow — contul dumneavoastră</h2><table>
        <tr><td class="k">Nume</td><td><b>${esc(r.displayName)}</b></td></tr>
        <tr><td class="k">Adresa</td><td>${esc(url)}</td></tr>
        <tr><td class="k">Utilizator</td><td class="pw">${esc(r.username)}</td></tr>
        <tr><td class="k">Parolă inițială</td><td class="pw">${esc(r.password)}</td></tr>
        <tr><td class="k">Compartiment</td><td>${esc((r.departments || []).join(', '))}</td></tr></table>
        <div class="note">${mustChange ? 'La prima autentificare vi se va cere să alegeți o parolă nouă. ' : ''}Nu comunicați parola altor persoane.</div></div>`).join('')}
      </body></html>`);
    w.document.close();
  };

  if (phase === 'choose' || phase === 'reading') return (
    <Modal title="Importă utilizatori din Excel" icon="users" onClose={onClose} footer={<><span className="grow" /><button className="btn" onClick={onClose}>Renunță</button></>}>
      {phase === 'reading' ? <div className="imp-reading"><span className="spinner" /><b>Se citește fișierul…</b></div> : (
        <div className="stack" style={{ gap: 14 }}>
          <p className="muted">Un tabel cu câte un rând pentru fiecare persoană. Coloane recunoscute: <b>Nume complet</b> (sau <b>Nume</b> + <b>Prenume</b>), <b>Utilizator</b>, <b>Email</b>, <b>Compartiment</b> (mai multe separate prin „;”), <b>Șef</b> (Da/Nu), <b>Rol</b>, <b>Funcție</b>. Compartimentele sunt potrivite cu organigrama chiar dacă sunt scrise puțin diferit.</p>
          <div className="row" style={{ gap: 8 }}>
            <a className="btn" href={api.authUrl('/api/auth/users/import/template')} download><Icon name="download" />Descarcă modelul Excel</a>
            <button className="btn primary" onClick={() => input.current?.click()}><Icon name="upload" />Alege fișierul (.xlsx sau .csv)</button>
            <input ref={input} type="file" hidden accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" onChange={e => { const f = e.target.files?.[0]; if (f) read(f); e.currentTarget.value = ''; }} />
          </div>
          <div className="banner small"><Icon name="info" size={15} /><span>Fiecare cont primește o parolă aleatorie (ex. <code>Brad-Lac-Soare-47</code>), afișată o singură dată la final, cu fișă de tipărit și Excel cu conturile.</span></div>
        </div>
      )}
    </Modal>
  );

  if (phase === 'done') return (
    <Modal title="Conturi create" icon="check" size="lg" onClose={onClose}
      footer={<><span className="warn-text small"><Icon name="alert" size={13} /> Parolele nu mai pot fi afișate după închiderea ferestrei.</span><span className="grow" />
        <button className="btn" onClick={printSlips} disabled={!created.length}><Icon name="file" />Tipărește fișele</button>
        <button className="btn" onClick={downloadXlsx} disabled={!created.length}><Icon name="download" />Descarcă Excel cu conturile</button>
        <button className="btn" onClick={sendMail} disabled={!withEmail.length || mailing || mailed != null} title={withEmail.length ? `Trimite utilizatorul și parola pe e-mail celor ${withEmail.length} persoane care au adresă` : 'Niciun cont creat nu are adresă de e-mail'}>
          <Icon name="mail" />{mailed != null ? `Trimise (${mailed})` : mailing ? 'Se trimit…' : `Trimite conturile pe e-mail${withEmail.length ? ` (${withEmail.length})` : ''}`}</button>
        <button className="btn primary" onClick={onClose}>Gata</button></>}>
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Nume</th><th>Utilizator</th><th>Parolă inițială</th><th>Compartimente</th><th>Rezultat</th></tr></thead>
          <tbody>{results.map((r, i) => (
            <tr key={i}><td>{r.displayName}{r.isHead && <span className="tag adm-tag-ok" style={{ marginLeft: 6 }}>șef</span>}</td><td className="mono">{r.username}</td>
              <td className="mono">{r.password ? <b>{r.password}</b> : '—'}</td><td className="small">{(r.departments || []).join(', ')}</td>
              <td>{r.status === 'created' ? <span className="ocr-state DONE">creat</span> : <span className="ocr-state FAILED" title={r.error}>{r.error || r.status}</span>}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </Modal>
  );

  const unknown = p!.unknownDepartments;
  const depOpts = [{ value: 'new', label: 'Creează unitate nouă' }, { value: 'skip', label: 'Ignoră' }, ...p!.departments.map(d => ({ value: String(d.id), label: d.name, sub: 'unitate existentă' }))];
  return (
    <Modal title="Importă utilizatori — verificare" icon="users" size="xl" onClose={onClose}
      footer={<>
        <Switch on={mustChange} onChange={setMustChange} label="Schimbarea parolei la prima autentificare" />
        <span className="grow" />
        <span className="muted small">{selected.length} din {rows.length} rânduri se importă</span>
        <button className="btn" onClick={() => setPhase('choose')} disabled={phase === 'saving'}>Înapoi</button>
        <button className="btn primary" onClick={doImport} disabled={phase === 'saving' || !selected.length}><Icon name="check" />{phase === 'saving' ? 'Se creează…' : `Creează ${selected.length} conturi`}</button>
      </>}>
      <div className="stack" style={{ gap: 12 }}>
        {unknown.length > 0 && (
          <div className="card imp-unknown">
            <div className="card-head"><Icon name="alert" /><h2>Compartimente care nu există în organigramă ({unknown.length})</h2><span className="grow" />
              <label className="row small" style={{ gap: 6 }}>Unitățile noi se creează sub
                <select className="select" style={{ width: 240 }} value={newParent ?? ''} onChange={e => setNewParent(e.target.value ? Number(e.target.value) : null)}>
                  <option value="">nivelul cel mai de sus</option>{p!.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select></label>
            </div>
            <div className="card-body">
              {unknown.map(u => (
                <div key={u} className="row imp-map"><b>{u}</b><Icon name="arrowRight" size={14} />
                  <div style={{ width: 340 }}><Combobox value={mapping[u] || 'new'} options={depOpts} clearable={false} onChange={v => setMapping(m => ({ ...m, [u]: String(v) }))} /></div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="table-wrap">
          <table className="table imp-users">
            <thead><tr><th style={{ width: 34 }} /><th>Nume</th><th>Utilizator</th><th>Email</th><th>Compartimente</th><th style={{ width: 60 }}>Șef</th><th style={{ width: 150 }}>Rol</th><th>Observații</th></tr></thead>
            <tbody>{rows.map((r, i) => (
              <tr key={i} className={r.status !== 'new' ? 'muted-row' : ''}>
                <td><input type="checkbox" checked={!!r.include} disabled={r.status === 'error'} onChange={e => upd(i, { include: e.target.checked })} /></td>
                <td><input className="input" value={r.displayName} onChange={e => upd(i, { displayName: e.target.value })} />{r.function && <div className="muted small">{r.function}</div>}</td>
                <td><input className={'input mono ' + (dupUsers.has(r.username) && r.include ? 'invalid' : '')} value={r.username} onChange={e => upd(i, { username: e.target.value.toLowerCase().replace(/\s+/g, '') })} /></td>
                <td className="small">{r.email || <span className="muted">—</span>}</td>
                <td>{r.departments.length ? r.departments.map((d, j) => (
                  <span key={j} className={'dep-chip ' + d.level} title={d.level === 'similar' ? `„${d.text}” → ${d.name}` : d.level === 'none' ? 'nu există în organigramă — vezi mai sus' : ''}>
                    {d.name || d.text}{d.level === 'similar' && <Icon name="check" size={11} />}{d.level === 'none' && <Icon name="alert" size={11} />}
                  </span>)) : <span className="muted small">—</span>}</td>
                <td><input type="checkbox" checked={r.isHead} onChange={e => upd(i, { isHead: e.target.checked })} title="Șeful primei unități din listă" /></td>
                <td><select className="select" value={r.roles.includes('ADMIN') ? 'ADMIN' : r.roles.includes('REGISTRY') ? 'REGISTRY' : 'DEPARTMENT'} onChange={e => upd(i, { roles: e.target.value === 'DEPARTMENT' ? ['DEPARTMENT'] : [e.target.value, 'DEPARTMENT'] })}>
                  {ROLE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></td>
                <td className="small">{r.status === 'exists' && <span className="tag">există</span>} {r.issues.map((x, k) => <div key={k} className={r.status === 'error' ? 'warn-text' : 'muted'}>{x}</div>)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
